import React, { useEffect, useState, useRef } from "react";
import { Box, Container, Typography, Button, Paper, Checkbox, FormControlLabel, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import rddMobilityIcon from "assets/icons/RDD_icons_version_2-outline/mobility_outline500px.png";
import rddClimateIcon from "assets/icons/RDD_icons_version_2-outline/climate_outline500px.png";
import rddAgriFoodIcon from "assets/icons/RDD_icons_version_2-outline/agri-food_outline500px.png";
import rddTourismIcon from "assets/icons/RDD_icons_version_2-outline/tourism_outline500px.png";
import rddCultureIcon from "assets/icons/RDD_icons_version_2-outline/culture_outline500px.png";
import rddLocalIcon from "assets/icons/RDD_icons_version_2-outline/local_outline500px.png";
import rieDiagramImg from "assets/images/RIE.png";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import default_theme from "assets/themes/themes";
import { blueTextFieldStyles } from "assets/themes/textFieldStyles";
import styles from "./StepZeroPage.module.css";
import {
  fetchProjectDetailRequest,
  fetchProjectRddsRequest,
  saveProjectRddsRequest,
  saveProjectRieRequest,
  deleteProjectRieSiteRequest
} from "../../../../redux/projects/projectsSlice";
import StepLayout from "../../../../components/StepsLayout/StepsLayout";
import { fetchRddsRequest } from "../../../../redux/rdds/rddSlice";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { InputAdornment, Divider, IconButton } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TooltipLabel from "components/Common/TooltipLabel";
import PrimaryOutlinedButton from "components/Common/PrimaryOutlinedButton";
import DangerButton from "components/Common/DangerButton";
import DocumentItem from "components/Documents/StepZeroDocuments/stepZeroDocument";
import RIE_ROLES, { RIE_PLACES, RIE_PRACTICES } from "../../../../constants/rieRoles";
import RieInfoCard from "../../../../components/RieRoles/RieInfoCard";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhaseArrow from "components/Common/PhaseArrow";
import FigmaBlueDivider from "components/StepComponents/FigmaBlueDivider"
import AddButton from "components/Common/AddButton";
import PDFChatPopup from "components/agents/PDFChatPopup";


const documents = [
  {
    id: "HANDBOOK_Del4.1_4",
    title: "RURACTIVE Handbook",
    file: "/documents/HANDBOOK_Del4.1_4.pdf"
  },
  {
    id: "RURACTIVE_D2.1_Final_040724",
    title: "D2.1 RURACTIVE Conceptual framework for innovation",
    file: "/documents/RURACTIVE_D2.1_Final_040724.pdf"
  },
  {
    id: "D4.1_RURACTIVE_final",
    title: "D4.1 Activating RIEs for community-led development and empowerment",
    file: "/documents/D4.1_RURACTIVE_final.pdf"
  },
];

const rddGuide = [
  {
    icon: rddMobilityIcon,
    name: "Sustainable Multimodal Mobility",
    tagline: "Connect people and places through sustainable mobility.",
    desc: "Explore approaches that improve rural accessibility through public, shared, flexible and active mobility, while reducing dependence on private cars.",
    popup: {
      title: "What does this RDD cover?",
      body: [
        "This RDD focuses on improving mobility and accessibility in rural areas through sustainable, shared, flexible and multimodal transport solutions.",
        "Rural communities can face long distances, limited public transport and strong dependence on private cars. RURACTIVE considers approaches that can make mobility more accessible while supporting environmental sustainability and social inclusion.",
      ],
      examples: [
        "Shared mobility and ride sharing",
        "Carpooling and shared taxis",
        "Flexible and demand-responsive transport",
        "Multimodal transport services",
        "Active travel such as walking and cycling",
      ],
    },
  },
  {
    icon: rddClimateIcon,
    name: "Energy Transition & Climate Neutrality",
    tagline: "Support the transition towards cleaner and more resilient energy systems.",
    desc: "Explore renewable energy, energy efficiency, energy communities and other approaches that can support rural areas in the transition towards climate neutrality.",
    popup: {
      title: "What does this RDD cover?",
      body: [
        "This RDD focuses on supporting rural areas in the transition towards renewable and more sustainable energy systems and climate neutrality.",
        "Rural areas have significant potential for renewable energy production and can benefit from new energy-related opportunities while supporting a fair transition.",
      ],
      examples: [
        "Renewable energy production",
        "Renewable energy communities",
        "Local energy generation and consumption",
        "Energy efficiency",
        "Energy microgrids and storage",
        "Community participation in the energy transition",
      ],
    },
  },
  {
    icon: rddAgriFoodIcon,
    name: "Sustainable Agri-food Systems & Ecosystem Management",
    tagline: "Develop sustainable food systems while protecting rural ecosystems.",
    desc: "Explore approaches across agriculture, food systems and ecosystem management that support resilient rural communities, sustainable livelihoods and healthy ecosystems.",
    popup: {
      title: "What does this RDD cover?",
      body: [
        "This RDD connects agricultural production, food systems and ecosystem management to support sustainable and resilient rural areas.",
        "It considers the transformation of agriculture and food systems while maintaining ecosystem health, biodiversity and sustainable livelihoods.",
      ],
      examples: [
        "Agroecological practices",
        "Sustainable agricultural production",
        "Sustainable food supply chains",
        "Ecosystem and agroecosystem management",
        "Resource recycling",
        "Nature-based approaches in agriculture",
      ],
    },
  },
  {
    icon: rddTourismIcon,
    name: "Nature-based & Cultural Tourism",
    tagline: "Build sustainable tourism around rural natural and cultural assets.",
    desc: "Explore ways to develop tourism based on nature, cultural heritage and local resources while respecting the needs of rural communities and their environments.",
    popup: {
      title: "What does this RDD cover?",
      body: [
        "This RDD focuses on tourism that builds on the natural and cultural resources of rural areas.",
        "RURACTIVE considers approaches that allow visitors to experience rural nature, heritage and local culture while supporting sustainable development and the needs of local communities.",
      ],
      examples: [
        "Nature-based tourism",
        "Cultural tourism",
        "Cultural and nature-based routes",
        "Sustainable destination development",
        "Interpretation of natural and cultural heritage",
        "Community-based tourism",
      ],
    },
  },
  {
    icon: rddCultureIcon,
    name: "Culture & Cultural Innovation",
    tagline: "Use culture, creativity and heritage to support rural development.",
    desc: "Explore cultural, creative and innovative activities that can strengthen communities, diversify rural economies and contribute to quality of life.",
    popup: {
      title: "What does this RDD cover?",
      body: [
        "This RDD considers culture, creativity, cultural heritage and innovation as resources for rural development.",
        "Culture can contribute to community vitality, economic diversification, quality of life and the development of new ideas, activities and solutions.",
      ],
      examples: [
        "Cultural and creative activities",
        "Cultural heritage management and conservation",
        "Community-led cultural initiatives",
        "Creative businesses and enterprises",
        "Digital innovation for culture",
        "New cultural products, services and experiences",
      ],
    },
  },
  {
    icon: rddLocalIcon,
    name: "Local Services, Health & Well-being",
    tagline: "Improve access to services and support rural well-being.",
    desc: "Explore approaches that improve access to healthcare, housing, education, digital and governance services, and other aspects of quality of life in rural areas.",
    popup: {
      title: "What does this RDD cover?",
      body: [
        "This RDD focuses on improving access to local services, healthcare and other conditions that support the well-being and quality of life of rural communities.",
        "It includes both physical and digital services and considers how innovation and technology can help overcome the challenges of distance, ageing and limited service provision.",
      ],
      examples: [
        "Healthcare and care services",
        "Support for older people and caregivers",
        "Housing",
        "Education",
        "E-governance",
        "Digital access and connectivity",
        "Smart and connected services",
      ],
    },
  },
];

const StepZeroPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const [selectedRDDs, setSelectedRDDs] = useState([]);
  const [rddDescriptions, setRddDescriptions] = useState({});

  const [coordinator, setCoordinator] = useState("");
  const [subCoordinator, setSubCoordinator] = useState("");

  const { currentProjectDetail, detailLoading, rddsSaving, rieLoading, error } = useSelector(
    (state) => state.projects
  );

  const { items: rdds, loading: rddsLoading } = useSelector(
    (state) => state.rdds
  );
  // console.log("RDDs:", rdds);

  const [sites, setSites] = useState([
    { address: "", postcode: "", country: "" }
  ]);

  const rddSectionRef = useRef(null);
  const rieSectionRef = useRef(null);

  const [openRddPopup, setOpenRddPopup] = useState(null);
  const [openRiePopup, setOpenRiePopup] = useState(null);
  const [wasSaving, setWasSaving] = useState(false);
  const [rddSaveError, setRddSaveError] = useState(null);
  const [rddSaveSuccess, setRddSaveSuccess] = useState(false);
  const [wasSavingRie, setWasSavingRie] = useState(false);
  const [rieSaveError, setRieSaveError] = useState(null);
  const [rieSaveSuccess, setRieSaveSuccess] = useState(false);

  // Watch rddsSaving transition: detect success/failure when saving completes
  useEffect(() => {
    if (wasSaving && !rddsSaving) {
      setWasSaving(false);
      if (error) {
        setRddSaveError(error);
      } else {
        setRddSaveSuccess(true);
      }
    }
  }, [wasSaving, rddsSaving, error]);

  // Watch rieLoading transition: detect success/failure when saving completes
  useEffect(() => {
    if (wasSavingRie && !rieLoading) {
      setWasSavingRie(false);
      if (error) {
        setRieSaveError(error);
      } else {
        setRieSaveSuccess(true);
      }
    }
  }, [wasSavingRie, rieLoading, error]);

  const scrollToRef = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const conceptCards = [
    {
      title: "Rural Development Drivers (RDD's)",
      tagline: "What are we working on?",
      desc: "Six development areas that guide the RURACTIVE methodology and around which the RIE is established.",
      label: "Learn about RDDs",
      action: () => scrollToRef(rddSectionRef),
    },
    {
      title: "Rural Innovation Ecosystem (RIE)",
      tagline: "What are we building?",
      desc: "A community of people, places and practices that shares interests in one or more RDDs.",
      label: "Learn about the RIE",
      action: () => scrollToRef(rieSectionRef),
    },
  ];

  useEffect(() => {
    dispatch(fetchRddsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!currentProjectDetail || currentProjectDetail.id !== projectId) {
      dispatch(fetchProjectDetailRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectRddsRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  // prefill RDDs
  useEffect(() => {
    if (currentProjectDetail?.rdds) {
      setSelectedRDDs(currentProjectDetail.rdds.map(r => r.rdd_id));

      const descriptions = {};
      currentProjectDetail.rdds.forEach(r => {
        descriptions[r.rdd_id] = r.description || "";
      });
      setRddDescriptions(descriptions);
    }
  }, [currentProjectDetail]);

  // prefill RIE
  useEffect(() => {
    if (currentProjectDetail?.rie) {
      setCoordinator(currentProjectDetail.rie.coordinator || "");
      setSubCoordinator(currentProjectDetail.rie.sub_coordinator || "");
    }
  }, [currentProjectDetail]);

  // prefill sites
  useEffect(() => {
    if (currentProjectDetail?.rie?.sites) {
      setSites(
        currentProjectDetail.rie.sites.length > 0
          ? currentProjectDetail.rie.sites.map(site => ({
            id: site.id,
            address: site.address || "",
            postcode: site.postcode || "",
            country: site.country || ""
          }))
          : [{ address: "", postcode: "", country: "" }]
      );
    }
  }, [currentProjectDetail]);

  const handleToggle = (id) => {
    setSelectedRDDs((prev) =>
      prev.includes(id) ? prev.filter((rdd) => rdd !== id) : [...prev, id]
    );
  };

  const handleDescriptionChange = (id, value) => {
    setRddDescriptions(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // save handler
  const handleSave = () => {
    setWasSaving(true);
    setRddSaveError(null);
    dispatch(saveProjectRddsRequest({
      projectId,
      rdds: selectedRDDs.map(id => ({
        rdd_id: id,
        description: rddDescriptions[id] || ""
      }))
    }));
  };

  // FIXED save payload
  const handleSaveRie = () => {
    setWasSavingRie(true);
    setRieSaveError(null);
    dispatch(saveProjectRieRequest({
      projectId,
      coordinator,
      sub_coordinator: subCoordinator,
      sites: sites.map(site => ({
        id: site.id,
        address: site.address,
        postcode: site.postcode,
        country: site.country
      }))
    }));
  };

  const handleSiteChange = (index, field, value) => {
    setSites((prev) =>
      prev.map((site, i) =>
        i === index ? { ...site, [field]: value } : site
      )
    );
  };

  const handleAddSite = () => {
    setSites((prev) => [
      ...prev,
      { address: "", postcode: "", country: "" }
    ]);
  };

  const handleRemoveSite = (site, index) => {
    // remove from UI
    setSites((prev) => prev.filter((_, i) => i !== index));

    // only call backend if it exists
    if (site.id) {
      dispatch(
        deleteProjectRieSiteRequest({
          projectId,
          siteId: site.id,
        })
      );
    }
  };

  useEffect(() => {
    console.log("RIE SITES:", currentProjectDetail?.rie?.sites);
  }, [currentProjectDetail]);

  if (!currentProjectDetail) {
    return <div>Loading project...</div>;
  }

  if (rddsLoading) {
    return <div>Loading RDDs...</div>;
  }

  return (
    <StepLayout step={0}>

      <Box sx={{ textAlign: "center" }}>


        <Typography variant="h5" className={styles.projectTitle}>
          Step 0: Getting Started
        </Typography>
      </Box>

      <Box sx={{ mt: 4, maxWidth: 900, mx: "auto" }}>

        {/* Introduction */}
        <Typography variant="h5" className={styles.introSubtitle}>
          Build the foundation for your Rural Innovation Ecosystem.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          Before working with stakeholders, establish a shared understanding of the
          RURACTIVE methodology and define the development priorities and territory
          around which your Rural Innovation Ecosystem will be developed.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          In this step, you will become familiar with the RURACTIVE approach, select
          the relevant Rural Development Drivers (RDDs), define your territory and
          begin setting up your Rural Innovation Ecosystem.
        </Typography>

        <Box className={styles.checklist}>
          <Typography variant="h6" className={styles.checklistTitle}>
            What you will complete
          </Typography>
          {[
            "Understand the RURACTIVE approach",
            "Select 2–4 Rural Development Drivers",
            "Define the reference territory",
            "Identify the RIE coordinator and RIE site(s)",
          ].map((item) => (
            <Box className={styles.checkItem} key={item}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Typography className={styles.checkText}>{item}</Typography>
            </Box>
          ))}
        </Box>

        <FigmaBlueDivider />

        {/* Phase 0.1 */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}>
          <PhaseArrow />
          Phase 0.1: Familiarizing with the guidelines
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: "#7898A4", mb: 3 }}
        >
          Before defining your Rural Innovation Ecosystem, take a few minutes to
          understand the main concepts behind the methodology. You will work with
          Rural Development Drivers, a Rural Innovation Ecosystem and a range of
          local stakeholders throughout the following steps.
        </Typography>

        {/* Concept cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 2.5,
            mb: 2,
          }}
        >
          {conceptCards.map((card) => (
            <Box key={card.title} className={styles.conceptCard}>
              <Typography variant="h6" className={styles.conceptTitle}>
                {card.title}
              </Typography>
              <Typography className={styles.conceptTagline}>
                {card.tagline}
              </Typography>
              <Typography variant="body2" className={styles.conceptDesc}>
                {card.desc}
              </Typography>
              <Button className={styles.conceptLink} onClick={card.action}>
                {card.label}
                <ArrowForwardOutlinedIcon className={styles.conceptLinkIcon} />
              </Button>
            </Box>
          ))}
        </Box>

        {/* Useful documents */}
        <Box className={styles.documentsBlock}>
          <Box className={styles.documentsHeader}>
            <MenuBookOutlinedIcon className={styles.documentsIcon} />
            <Typography variant="h6" className={styles.documentsTitle}>
              Useful documents
            </Typography>
          </Box>
          <Typography variant="body2" className={styles.documentsHint}>
            The tool provides the information you need to complete the process. You can also consult the project documentation for more detailed guidance.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1.5 }}>
            {documents.map((doc, index) => (
              <DocumentItem
                key={index}
                title={doc.title}
                file={doc.file}
                highlight={index === 0}
              />
            ))}
          </Box>
          {/*<PDFChatPopup projectId={projectId} documentIds={documents.map(d => d.id)} />*/}
          <PDFChatPopup projectId={projectId} documentIds={["HANDBOOK_Del4.1_4"]} />
        </Box>

        <FigmaBlueDivider />

        {/* Phase 0.2 */}
        <Box ref={rddSectionRef} sx={{ mt: 6 }}>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}>
            <PhaseArrow />
            Phase 0.2: Identifying preliminary rural development drivers and territories
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "#7898A4", mb: 4 }}
          >
            Select the Rural Development Drivers (RDDs) that are most relevant for your territory or the current project you are working on, and include a short explanation of why you have selected them.
          </Typography>

          {/* RDD guidance */}
          <Typography variant="h5" className={styles.rddGuideTitle}>
            What are Rural Development Drivers?
          </Typography>
          <Typography variant="body1" className={styles.rddGuideText}>
            Rural Development Drivers (RDDs) are the six rural development areas that form the foundation of the RURACTIVE methodology.
          </Typography>
          <Typography variant="body1" className={styles.rddGuideText}>
            Your Rural Innovation Ecosystem will focus on one or more of these drivers. Understanding them will help you decide which areas are most relevant to your project and territory.
          </Typography>

          <Box
            component="img"
            src="/rdd-images/RURACTIVE_RDDGraphic_300px.png"
            alt="Agriculture"
            sx={{ width: "100%", height: 300 }}
          />

          <Typography variant="h6" className={styles.rddGuideSubtitle}>
            Explore the RDDs
          </Typography>
          <Typography variant="body1" className={styles.rddGuideText}>
            Select Learn more on each card to understand what the RDD covers and explore examples of the types of activities and solutions it can include.
          </Typography>

          <Box className={styles.rddGuideGrid}>
            {rddGuide.map((rdd) => (
              <Box key={rdd.name} className={styles.rddGuideCard}>
                <Box className={styles.rddGuideHeader}>
                  <Box
                    component="img"
                    src={rdd.icon}
                    alt={rdd.name}
                    className={styles.rddGuideIcon}
                  />
                </Box>
                <Typography variant="h6" className={styles.rddGuideName}>
                  {rdd.name}
                </Typography>
                <Typography className={styles.rddGuideTagline}>
                  {rdd.tagline}
                </Typography>
                <Typography variant="body2" className={styles.rddGuideDesc}>
                  {rdd.desc}
                </Typography>
                <Button
                  className={styles.rddGuideLink}
                  onClick={() => setOpenRddPopup(rdd)}
                >
                  Learn more
                  <ArrowForwardOutlinedIcon className={styles.conceptLinkIcon} />
                </Button>
              </Box>
            ))}
          </Box>

          {/* Before you select */}
          <Box className={styles.beforeSelectPanel}>
            <Typography className={styles.beforeSelectEyebrow}>
              Before you select
            </Typography>
            <Typography variant="h5" className={styles.beforeSelectTitle}>
              Which RDDs are most relevant to your project?
            </Typography>
            <Typography variant="body1" className={styles.beforeSelectText}>
              The RDDs you select should reflect the development priorities and
              opportunities of your territory.
            </Typography>

            <Typography variant="h6" className={styles.beforeSelectSubtitle}>
              Think about:
            </Typography>

            <Box className={styles.beforeSelectList}>
              {[
                {
                  question: "What matters in your territory?",
                  answer:
                    "Which rural development challenges or opportunities are particularly important?",
                },
                {
                  question: "What is already happening?",
                  answer:
                    "Which RDDs are already being addressed through projects, policies or initiatives?",
                },
                {
                  question: "Where is there potential?",
                  answer:
                    "Which areas could benefit from further development or innovation?",
                },
                {
                  question: "Where are the connections?",
                  answer: "Could working on one RDD create synergies with another?",
                },
              ].map((item) => (
                <Box className={styles.beforeSelectItem} key={item.question}>
                  <Typography className={styles.beforeSelectQuestion}>
                    {item.question}
                  </Typography>
                  <Typography variant="body2" className={styles.beforeSelectAnswer}>
                    {item.answer}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box className={styles.beforeSelectTip}>
              <Typography variant="body2" className={styles.beforeSelectTipText}>
                <strong>💡 Tip:</strong> RDDs can be interconnected. Consider the
                relationships between them rather than looking at each driver in
                isolation.
              </Typography>
            </Box>
          </Box>

          <Box className={styles.phaseDataPanel} sx={{ mt: 4 }}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Box>
                <Typography className={styles.phaseSectionTitle}>Select RDDs</Typography>
                <Typography className={styles.phaseSectionSubtitle}>
                  Choose the development priorities for your project
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}>
              Choose the focus of your project
            </Typography>

            <Typography sx={{ mb: 2, color: "#7898A4" }}>
              Select 2–4 Rural Development Drivers that are most relevant to your project.
              Briefly explain why each selected RDD is relevant to your territory and project.
            </Typography>

            <Typography sx={{ mb: 2, color: "#35B3AB", fontWeight: 600 }}>
              {selectedRDDs.length} RDD{selectedRDDs.length !== 1 ? "s" : ""} selected
            </Typography>

            {rdds.map((rdd) => {
              const isSelected = selectedRDDs.includes(rdd.id);

              return (
                <Box
                  key={rdd.id}
                  onClick={() => handleToggle(rdd.id)}
                  sx={{
                    border: `2px solid ${isSelected ? rdd.color : "#B8D4E3"}`,
                    borderRadius: 3,
                    p: 2,
                    mb: 2,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: isSelected ? `${rdd.color}10` : "#fff",

                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 3,
                    },
                  }}
                >
                  {/* CONTENT ROW */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

                    {/* IMAGE */}
                    {rdd.image_url && (
                      <Box
                        component="img"
                        src={rdd.image_url}
                        alt={rdd.name}
                        sx={{
                          width: 50,
                          height: 50,
                          objectFit: "contain",
                        }}
                      />
                    )}

                    {/* TEXT */}
                    <Typography
                      sx={{
                        color: rdd.color,
                        fontWeight: 600,
                        flex: 1,
                      }}
                    >
                      {rdd.name}
                    </Typography>

                    {/* CHECKBOX (visual only) */}
                    <Checkbox
                      checked={isSelected}
                      sx={{
                        color: rdd.color,
                        '&.Mui-checked': {
                          color: rdd.color,
                        },
                      }}
                    />
                  </Box>

                  {/* DESCRIPTION FIELD */}
                  {isSelected && (
                    <TextField
                      fullWidth
                      placeholder="Describe why this RDD is relevant to the project..."
                      multiline
                      minRows={2}
                      maxRows={5}
                      value={rddDescriptions[rdd.id] || ""}
                      onClick={(e) => e.stopPropagation()} // prevent card toggle
                      onChange={(e) =>
                        handleDescriptionChange(rdd.id, e.target.value)
                      }
                      sx={{
                        ...blueTextFieldStyles(default_theme),
                        mt: 2,
                        opacity: isSelected ? 1 : 0,
                        maxHeight: isSelected ? 200 : 0,
                        overflow: "hidden",
                        transition: "all 0.3s ease"
                      }}
                    />
                  )}
                </Box>
              );
            })}

            <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
              <PrimaryOutlinedButton
                onClick={handleSave}
                disabled={wasSaving || rddsSaving}
                startIcon={wasSaving || rddsSaving ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {wasSaving || rddsSaving ? "Saving..." : "Save Selection"}
              </PrimaryOutlinedButton>
            </Box>
          </Box>
        </Box>

        <FigmaBlueDivider />

        {/* Phase 0.3 RIE SECTION */}
        <Box ref={rieSectionRef} sx={{ mt: 6 }}>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}>
            <PhaseArrow />
            Phase 0.3 — Set up your RIE
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 2 }}>
            Start building your Rural Innovation Ecosystem
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Your Rural Innovation Ecosystem (RIE) brings together people, places and practices
            around one or more Rural Development Drivers.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
            In this phase, you will define the people responsible for coordinating the RIE
            and the places where the community can meet and collaborate.
          </Typography>

          {/* ── INFO PANEL (learn) ── */}
          <Box className={styles.phaseInfoPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Box>
                <Typography className={styles.phaseSectionTitle}>Learn</Typography>
                <Typography className={styles.phaseSectionSubtitle}>
                  Understand how the RIE is structured
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
              What is a Rural Innovation Ecosystem?
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
              A Rural Innovation Ecosystem is a community of people, places and practices
              that share interests in one or more Rural Development Drivers.
            </Typography>
            <Box
              component="img"
              src={rieDiagramImg}
              alt="Rural Innovation Ecosystem diagram"
              sx={{ width: "100%", maxHeight: 400, objectFit: "contain", borderRadius: 2, mb: 3 }}
            />

            <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 2 }}>
              How the RIE works
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
              {[
                {
                  key: "people",
                  icon: <PersonOutlineIcon sx={{ fontSize: 32, color: "#35B3AB" }} />,
                  title: "People",
                  desc: "Stakeholders and community members involved in the RIE.",
                },
                {
                  key: "places",
                  icon: <LocationOnOutlinedIcon sx={{ fontSize: 32, color: "#35B3AB" }} />,
                  title: "Places",
                  desc: "The territory and locations where the RIE community meets and collaborates.",
                },
                {
                  key: "practices",
                  icon: <PsychologyOutlinedIcon sx={{ fontSize: 32, color: "#35B3AB" }} />,
                  title: "Practices",
                  desc: "The activities, knowledge and approaches through which the RIE develops and collaborates.",
                },
              ].map((item) => (
                <Box
                  key={item.key}
                  onClick={() => setOpenRiePopup(item.key)}
                  sx={{
                    p: 3, borderRadius: 3,
                    border: "1px solid #B8D4E3",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(35,87,107,0.06)",
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "translateY(-3px)", boxShadow: "0 6px 20px rgba(35,87,107,0.12)" },
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>{item.icon}</Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#7898A4" }}>
                    {item.desc}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#35B3AB", fontWeight: 600, mt: 1.5, fontSize: "0.8rem" }}>
                    Learn more →
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* RIE popup dialog */}
          <Dialog
            open={!!openRiePopup}
            onClose={() => setOpenRiePopup(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
              {openRiePopup === "people" && "The People of the RIE"}
              {openRiePopup === "places" && "The Places of the RIE"}
              {openRiePopup === "practices" && "The Practices of the RIE"}
            </DialogTitle>
            <DialogContent dividers>
              {openRiePopup === "people" && (
                <Box>
                  <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
                    A Rural Innovation Ecosystem brings together people and organisations
                    with different roles and contributions.
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
                    {RIE_ROLES.map((role) => (
                      <RieInfoCard key={role.title} item={role} compact />
                    ))}
                  </Box>
                </Box>
              )}
              {openRiePopup === "places" && (
                <Box>
                  <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
                    The places of the RIE define the geographical scope and physical spaces where
                    the community comes together.
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {RIE_PLACES.map((place) => (
                      <RieInfoCard key={place.title} item={place} compact />
                    ))}
                  </Box>
                </Box>
              )}
              {openRiePopup === "practices" && (
                <Box>
                  <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
                    Practices are the activities, knowledge and approaches through which the RIE
                    develops and collaborates to co-create innovative rural solutions.
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {RIE_PRACTICES.map((practice) => (
                      <RieInfoCard key={practice.title} item={practice} compact />
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenRiePopup(null)} sx={{ color: "#23576B", fontWeight: 600 }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* ── DATA ENTRY PANEL (do) ── */}
          <Box className={styles.phaseDataPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Box>
                <Typography className={styles.phaseSectionTitle}>Set up</Typography>
                <Typography className={styles.phaseSectionSubtitle}>
                  Define your coordination and meeting spaces
                </Typography>
              </Box>
            </Box>

            {/* Coordinator */}
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B", mb: 0.5 }}>
              Set up the RIE coordination
            </Typography>
            <Typography variant="body2" sx={{ color: "#7898A4", mb: 0.5 }}>
              Who will coordinate your RIE?
            </Typography>
            <Typography variant="body2" sx={{ color: "#7898A4", mb: 2 }}>
              The coordination team is responsible for leading the RIE, organising activities
              and ensuring continuity throughout the co-development process.
            </Typography>

            <Box className={styles.phaseFieldCard} sx={{ mb: 2 }}>
              <Typography className={styles.phaseFieldLabel}>RIE Coordinator</Typography>
              <Typography className={styles.phaseFieldDesc}>
                The person or organisation responsible for coordinating the activities of the Rural Innovation Ecosystem.
              </Typography>
              <TextField
                placeholder="Enter RIE Coordinator"
                value={coordinator}
                onChange={(e) => setCoordinator(e.target.value)}
                fullWidth
                sx={blueTextFieldStyles(default_theme)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: default_theme.palette.other.figmaBlue }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {/* Sub-coordinator */}
            <Box className={styles.phaseFieldCard} sx={{ mb: 3 }}>
              <Typography className={styles.phaseFieldLabel}>Sub-coordinator</Typography>
              <Typography className={styles.phaseFieldDesc}>
                A person who supports the RIE Coordinator in coordinating the activities of the Rural Innovation Ecosystem.
              </Typography>
              <TextField
                placeholder="Enter Sub-coordinator"
                value={subCoordinator}
                onChange={(e) => setSubCoordinator(e.target.value)}
                fullWidth
                sx={blueTextFieldStyles(default_theme)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: default_theme.palette.other.figmaBlue }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            {/* Sites divider */}
            <Divider sx={{ my: 1, borderColor: "rgba(35,87,107,0.1)" }} />

            {/* RIE Sites */}
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B", mt: 2, mb: 0.5 }}>
              Define your RIE Sites
            </Typography>
            <Typography variant="body2" sx={{ color: "#7898A4", mb: 0.5 }}>
              Where will the RIE community meet?
            </Typography>
            <Typography variant="body2" sx={{ color: "#7898A4", mb: 2 }}>
              RIE Sites are places where the RIE community can meet, exchange ideas and
              experiences, and participate in workshops and co-development activities.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 1.5 }}>
              Your RIE Sites
            </Typography>

            {sites.map((site, index) => (
              <Box key={site.id || index} className={styles.phaseSiteCard} sx={{ mb: 1.5 }}>
                <TextField
                  label="Address"
                  value={site.address}
                  onChange={(e) => handleSiteChange(index, "address", e.target.value)}
                  sx={{ ...blueTextFieldStyles(default_theme), flex: 1 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnOutlinedIcon sx={{ color: default_theme.palette.other.figmaBlue }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label="Postcode"
                  value={site.postcode}
                  onChange={(e) => handleSiteChange(index, "postcode", e.target.value)}
                  sx={{ ...blueTextFieldStyles(default_theme), flex: 1 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnOutlinedIcon sx={{ color: default_theme.palette.other.figmaBlue }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label="Country"
                  value={site.country}
                  onChange={(e) => handleSiteChange(index, "country", e.target.value)}
                  sx={{ ...blueTextFieldStyles(default_theme), flex: 1 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnOutlinedIcon sx={{ color: default_theme.palette.other.figmaBlue }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveSite(site, index)}
                  sx={{
                    alignSelf: "center",
                    color: "error.main",
                    "&:hover": { backgroundColor: "rgba(255,0,0,0.08)" },
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            ))}

            <Box sx={{ mt: 1, mb: 3 }}>
              <AddButton onClick={handleAddSite}>
                Add Site
              </AddButton>
            </Box>

            <Divider sx={{ my: 1, borderColor: "rgba(35,87,107,0.1)" }} />

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <PrimaryOutlinedButton
                onClick={handleSaveRie}
                disabled={wasSavingRie || rieLoading}
                startIcon={wasSavingRie || rieLoading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {wasSavingRie || rieLoading ? "Saving..." : "Save RIE"}
              </PrimaryOutlinedButton>
            </Box>
          </Box>
        </Box>

      </Box>

      {/* RDD popup */}
      <Dialog
        open={!!openRddPopup}
        onClose={() => setOpenRddPopup(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={styles.rddPopupTitle}>
          {openRddPopup?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" className={styles.rddPopupSubtitle}>
            {openRddPopup?.popup.title}
          </Typography>
          {openRddPopup?.popup.body.map((paragraph, i) => (
            <Typography key={i} variant="body2" className={styles.rddPopupBody}>
              {paragraph}
            </Typography>
          ))}
          <Typography variant="subtitle1" className={styles.rddPopupSubtitle}>
            Examples
          </Typography>
          <Box component="ul" className={styles.rddPopupList}>
            {openRddPopup?.popup.examples.map((example, i) => (
              <Box component="li" key={i} className={styles.rddPopupListItem}>
                <Typography variant="body2">{example}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            className={styles.rddPopupClose}
            onClick={() => setOpenRddPopup(null)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={rddSaveSuccess}
        autoHideDuration={3000}
        onClose={() => setRddSaveSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setRddSaveSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 999 }}
        >
          RDD selection saved successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!rddSaveError}
        autoHideDuration={5000}
        onClose={() => setRddSaveError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setRddSaveError(null)}
          severity="error"
          variant="filled"
          sx={{ borderRadius: 999 }}
        >
          {rddSaveError || "Failed to save RDD selection"}
        </Alert>
      </Snackbar>

      <Snackbar
        open={rieSaveSuccess}
        autoHideDuration={3000}
        onClose={() => setRieSaveSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setRieSaveSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 999 }}
        >
          RIE saved successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!rieSaveError}
        autoHideDuration={5000}
        onClose={() => setRieSaveError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setRieSaveError(null)}
          severity="error"
          variant="filled"
          sx={{ borderRadius: 999 }}
        >
          {rieSaveError || "Failed to save RIE"}
        </Alert>
      </Snackbar>
    </StepLayout>
  );
};

export default StepZeroPage;